import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export const ChangePasswordSection = () => {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user || user === false || (user.provider || "local") !== "local") return null;

  const submit = async () => {
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Use your new password next time you log in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="change-password-section">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-[#FF9F0A]" />
        <h2 className="font-bold text-lg">Change password</h2>
      </div>
      <p className="mt-2 text-sm text-neutral-400">
        Enter your current password, then choose a new one (minimum 8 characters).
      </p>
      <div className="mt-4 space-y-3 max-w-sm">
        <Input
          type="password"
          autoComplete="current-password"
          data-testid="cp-current-input"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password"
          className="bg-[#121212] border-[#27272A]"
        />
        <Input
          type="password"
          autoComplete="new-password"
          data-testid="cp-new-input"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 8 characters)"
          className="bg-[#121212] border-[#27272A]"
        />
        <Input
          type="password"
          autoComplete="new-password"
          data-testid="cp-confirm-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Repeat new password"
          className="bg-[#121212] border-[#27272A]"
        />
        <Button
          onClick={submit}
          disabled={saving || !current || next.length < 8 || !confirm}
          data-testid="cp-submit-btn"
          className="rounded-full bg-white text-black hover:bg-neutral-200 h-10 px-6 font-semibold"
        >
          {saving ? "Saving…" : "Change password"}
        </Button>
      </div>
    </div>
  );
};
